// src/client/components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesOccupancySelect.tsx

import React, { useCallback, useMemo, useRef } from 'react'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { validateSemiForm } from 'lib/forms/formUtils'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import config from 'constants/config'
import Backdrop from 'components/utils/Backdrop'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { MAX_TRAVELLERS_FOR_MALDIVES_CHARTER } from 'constants/charterFlight'

interface Props {
  rooms: Array<App.Occupants>
  open: boolean
  onRoomChange: (rooms: Array<App.Occupants>) => void
  onApply: () => void
  onClose: () => void
  triggerRef: React.RefObject<HTMLButtonElement | null>
  boundaryRef?: React.RefObject<HTMLElement>
  dropdownRef?: React.RefObject<HTMLDivElement | null>
  className?: string
}

function MaldivesOccupancySelect(props: Props) {
  const {
    rooms,
    open,
    onRoomChange,
    onApply,
    onClose,
    triggerRef,
    boundaryRef,
    dropdownRef: externalDropdownRef,
    className,
  } = props

  const internalDropdownRef = useRef<HTMLDivElement>(null)
  const dropdownRef = (externalDropdownRef ?? internalDropdownRef) as React.RefObject<HTMLDivElement>

  const totalTravellers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  // Disable individual +1 buttons when at max (9)
  const maxTravellersReached = totalTravellers >= MAX_TRAVELLERS_FOR_MALDIVES_CHARTER

  const onApplyClose = useCallback(() => {
    if (validateSemiForm(dropdownRef.current)) {
      onApply()
    }
  }, [dropdownRef, onApply])

  return (
    <div className={className}>
      <DropdownSheet
        ref={dropdownRef}
        data-testid="maldives-occupancy-select-dropdown"
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        boundaryRef={boundaryRef}
        size="L"
        title="Who is going?"
        open={open}
        onClose={onClose}
        placement="bottom-start"
        primaryActionProps={{
          children: 'Apply',
          onClick: onApplyClose,
        }}
        footerStart={
          <Group direction="horizontal-reverse">
            <BodyText variant="large">
              <FormattedOccupancy occupancies={rooms} saleUnit="room" />
            </BodyText>
          </Group>
        }
      >
        <OccupancyPicker
          saleUnit="room"
          occupancies={rooms}
          onChange={onRoomChange}
          enableMultiRoom={config.accommodation.allowsMultiRoom}
          childVariant="child-age"
          disableAddOccupants={maxTravellersReached}
          maxOccupants={MAX_TRAVELLERS_FOR_MALDIVES_CHARTER}
        />
      </DropdownSheet>
      <Backdrop visible={open} onClick={onClose} data-testid="occupancy-select-backdrop" />
    </div>
  )
}

export default MaldivesOccupancySelect
